import { useRef, useEffect, useLayoutEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Cpu, Layers, Smartphone, Workflow,
  Lightbulb, Box, LayoutDashboard, Rocket, ChevronDown,
} from "lucide-react";
import { useLang } from "@/i18n/translations";
import { gsap, usePrefersReducedMotion } from "@/hooks/useSmoothScroll";
import { PROJECTS, DEALALERT_ASSETS } from "@/lib/siteConfig";

const ease = [0.22, 1, 0.36, 1];
const MODULE_ICONS = [Cpu, Layers, Smartphone, Workflow];
const STEP_ICONS = [Lightbulb, Box, Cpu, Smartphone, LayoutDashboard, Rocket];

// Spatial positions of the 4 modules in the 3D scene (Szene 3)
const MODULE_POS = [
  { x: -350, y: -60, z: 120, rotateY: 9 },    // AI — left front
  { x: 330, y: -130, z: -160, rotateY: -12 }, // SaaS — right back
  { x: 305, y: 120, z: 60, rotateY: -8 },     // Mobile phone — right front
  { x: -310, y: 145, z: -80, rotateY: 8 },    // Automation — left back
];

// Cinematic entry: modules grow out of the arriving words (Match-Cut),
// approaching from depth at their final spot.
const MODULE_DEPTH_IN = 260;

const useIsDesktop = () => {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const fn = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return d;
};

/* ---------- shared building blocks ---------- */

const BigWordmark = ({ as: Tag = "h1", className = "" }) => (
  <Tag
    className={`font-display font-semibold tracking-[-0.035em] text-foreground leading-none ${className}`}
    data-testid="intro-wordmark"
  >
    CloudService
    <span className="text-[0.5em] ml-1 text-[#00d4ff] font-mono-cs font-normal align-baseline">.hr</span>
  </Tag>
);

const AutomationFlow = () => (
  <svg className="w-full h-10 mt-3" viewBox="0 0 200 40" aria-hidden="true">
    <path
      d="M8 20 C 48 4, 80 36, 120 20 S 176 8, 192 20"
      fill="none"
      stroke="rgba(0,212,255,0.5)"
      strokeWidth="1.5"
      strokeDasharray="5 8"
      className="cs-dash-flow"
    />
    <circle cx="8" cy="20" r="3" fill="#00d4ff" opacity="0.85" />
    <circle cx="120" cy="20" r="2.2" fill="#00d4ff" opacity="0.5" />
    <circle cx="192" cy="20" r="3" fill="#00d4ff" opacity="0.85" />
  </svg>
);

// Glass panel module (AI / SaaS / Automation)
const ModulePanel = ({ item, icon: Icon, withFlow = false, dim = false, testId }) => (
  <div
    data-testid={testId}
    className={`cs-glass rounded-2xl p-5 w-[252px] ${dim ? "opacity-90 blur-[1.5px]" : ""}`}
  >
    <div className="w-10 h-10 rounded-xl bg-[#00d4ff]/10 border border-[#00d4ff]/20 flex items-center justify-center">
      <Icon className="w-5 h-5 text-[#00d4ff]" />
    </div>
    <h3 className="mt-3.5 font-display text-lg font-semibold text-foreground">{item.title}</h3>
    <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">{item.text}</p>
    {withFlow && <AutomationFlow />}
  </div>
);

// Phone silhouette module (Mobile Apps)
const PhoneModule = ({ item, testId }) => (
  <div
    data-testid={testId}
    className="w-[164px] rounded-[2.1rem] border border-[rgba(167,243,255,0.22)] bg-[#0a0e17]/80 backdrop-blur-md p-2.5 shadow-[0_18px_60px_rgba(0,0,0,0.55),0_0_28px_rgba(0,212,255,0.08)]"
  >
    <div className="rounded-[1.6rem] bg-[#05070d] border border-white/5 px-4 pt-9 pb-7 relative overflow-hidden">
      <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-14 h-1.5 rounded-full bg-white/10" aria-hidden="true" />
      <div className="w-9 h-9 rounded-xl bg-[#00d4ff]/10 border border-[#00d4ff]/20 flex items-center justify-center">
        <Smartphone className="w-4.5 h-4.5 text-[#00d4ff]" />
      </div>
      <h3 className="mt-3 font-display text-base font-semibold text-foreground leading-tight">{item.title}</h3>
      <p className="mt-1.5 text-[11px] text-muted-foreground leading-relaxed">{item.text}</p>
      <div className="mt-4 space-y-1.5" aria-hidden="true">
        <div className="h-1.5 rounded-full bg-white/8 w-full" />
        <div className="h-1.5 rounded-full bg-white/8 w-3/4" />
        <div className="h-1.5 rounded-full bg-[#00d4ff]/25 w-1/2" />
      </div>
    </div>
  </div>
);

// Proof chip (Szene 5) — names only, DealAlert gets its real logo (dezent)
const ProofChip = ({ project, innerRef }) => (
  <div
    ref={innerRef}
    data-testid={`intro-chip-${project.id}`}
    className="cs-glass rounded-full pl-3 pr-5 py-2.5 flex items-center gap-3 will-change-transform"
  >
    {project.id === "dealalert" ? (
      <img
        src={DEALALERT_ASSETS.logo}
        alt="DealAlert Logo"
        width="28"
        height="28"
        loading="lazy"
        className="w-7 h-7 rounded-full object-contain bg-white/5 p-0.5"
      />
    ) : (
      <span
        className="w-7 h-7 rounded-full border border-white/10 flex items-center justify-center"
        aria-hidden="true"
      >
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: project.accent }} />
      </span>
    )}
    <span className="font-display text-sm font-medium text-foreground whitespace-nowrap">{project.name}</span>
  </div>
);

/* =====================================================================
   DESKTOP — Pinned, scroll-scrubbed Brand Intro Film (5 Szenen)
   ===================================================================== */
const IntroFilmDesktop = () => {
  const { t } = useLang();
  const sec = useRef(null);
  const camera = useRef(null);
  const bgRef = useRef(null);
  const glowRef = useRef(null);
  const s1 = useRef(null);
  const s2 = useRef(null);
  const s3 = useRef(null);
  const s4 = useRef(null);
  const s5 = useRef(null);
  const s2Kicker = useRef(null);
  const s2Tail = useRef(null);
  const lineRefs = useRef([]);
  const s3Head = useRef(null);
  const moduleRefs = useRef([]);
  const s3LineRefs = useRef([]);
  const s4Head = useRef(null);
  const s4Path = useRef(null);
  const nodeRefs = useRef([]);
  const s4Title = useRef(null);
  const s5Head = useRef(null);
  const chipRefs = useRef([]);
  const s5Hint = useRef(null);
  const dotRefs = useRef([]);
  const spark = useRef(null);
  const [scene, setScene] = useState(0);
  const sceneRef = useRef(0);
  const boundsRef = useRef([0.2, 0.4, 0.6, 0.8]);

  // useLayoutEffect statt useEffect: Das Cleanup (ctx.revert -> Unpin des
  // pin-spacers) muss synchron VOR der DOM-Entfernung laufen, sonst crasht
  // React beim Desktop->Mobile-Wechsel mit removeChild (schwarze Seite).
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const lines = lineRefs.current.filter(Boolean);
      const modules = moduleRefs.current.filter(Boolean);
      const s3Lines = s3LineRefs.current.filter(Boolean);
      const nodes = nodeRefs.current.filter(Boolean);
      const chips = chipRefs.current.filter(Boolean);
      const dots = dotRefs.current.filter(Boolean);

      // ---------- Match-Cut Messung (VOR allen Transforms, natürliches Layout) ----------
      // Wohin muss jedes Wort reisen, um "sein" Modul zu werden?
      // Wo liegen die Prozess-Nodes, auf denen die Modul-Dots landen?
      const sRect = sec.current.getBoundingClientRect();
      const cx = sRect.left + sRect.width / 2;
      const cy = sRect.top + sRect.height / 2;
      const lineTargets = lines.map((ln, i) => {
        const r = ln.getBoundingClientRect();
        return {
          x: cx + MODULE_POS[i].x - (r.left + r.width / 2),
          y: cy + MODULE_POS[i].y - (r.top + r.height / 2),
        };
      });
      const nodeTargets = nodes.map((n) => {
        const el = n.firstElementChild || n;
        const r = el.getBoundingClientRect();
        return { x: r.left + r.width / 2 - cx, y: r.top + r.height / 2 - cy };
      });
      const driftFor = (i) => (MODULE_POS[i].z > 0 ? 30 : 12);

      // Spark-Route: Wordmark (.hr) → links neben jedem Statement → Netzwerk-Zentrum →
      // Prozesslinie → Proof-Headline → Referenzen-Hint
      const wmEl = sec.current.querySelector('[data-testid="intro-wordmark"]');
      const wmRect = wmEl ? wmEl.getBoundingClientRect() : null;
      const sparkStart = wmRect
        ? { x: wmRect.right - cx + 28, y: wmRect.top + wmRect.height / 2 - cy }
        : { x: 0, y: -20 };
      const lineSparks = lines.map((ln) => {
        const r = ln.getBoundingClientRect();
        return { x: r.left - cx - 30, y: r.top + r.height / 2 - cy };
      });

      // ---------- initial states ----------
      gsap.set([s2.current, s3.current, s4.current, s5.current], { autoAlpha: 0 });
      lines.forEach((ln, i) => gsap.set(ln, { autoAlpha: 0, z: -460, y: 36, x: i % 2 ? 150 : -150 }));
      gsap.set(s2Kicker.current, { autoAlpha: 0, y: 24, x: -30 });
      gsap.set(s2Tail.current, { autoAlpha: 0 });
      gsap.set(s3Head.current, { autoAlpha: 0, y: 30 });
      modules.forEach((m, i) =>
        gsap.set(m, {
          xPercent: -50,
          yPercent: -50,
          x: MODULE_POS[i].x,
          y: MODULE_POS[i].y,
          z: MODULE_POS[i].z - MODULE_DEPTH_IN,
          rotateY: MODULE_POS[i].rotateY,
          scale: 0.42,
          autoAlpha: 0,
        })
      );
      gsap.set(s3Lines, { strokeDashoffset: 100 });
      gsap.set(s4Head.current, { autoAlpha: 0, y: 30 });
      gsap.set(s4Path.current, { strokeDashoffset: 100 });
      nodes.forEach((n, i) => gsap.set(n, { autoAlpha: 0, scale: 0.5, y: 26, x: i % 2 ? 40 : -40 }));
      gsap.set(s4Title.current, { autoAlpha: 0, y: 24, scale: 0.94 });
      gsap.set(s5Head.current, { autoAlpha: 0, y: 14, scale: 0.92 });
      chips.forEach((c, i) => gsap.set(c, { autoAlpha: 0, y: 26, z: -120, x: i % 2 ? 60 : -60 }));
      gsap.set(s5Hint.current, { autoAlpha: 0 });
      gsap.set(dots, { xPercent: -50, yPercent: -50, autoAlpha: 0 });
      gsap.set(spark.current, { xPercent: -50, yPercent: -50, autoAlpha: 0, x: sparkStart.x, y: sparkStart.y });

      // ---------- master timeline (pinned + scrubbed) ----------
      const tl = gsap.timeline({
        defaults: { ease: "none" },
        scrollTrigger: {
          trigger: sec.current,
          start: "top top",
          end: "+=340%",
          scrub: 1,
          pin: true,
          anticipatePin: 1,
          onUpdate: (self) => {
            const b = boundsRef.current;
            let idx = 0;
            for (let i = 0; i < b.length; i++) if (self.progress >= b[i]) idx = i + 1;
            if (idx !== sceneRef.current) {
              sceneRef.current = idx;
              setScene(idx);
            }
          },
        },
      });

      /* ----- Beat 1: hold, dann fliegt die Kamera durch das Wordmark ----- */
      tl.to({}, { duration: 0.75 });
      tl.to(s1.current, { autoAlpha: 0, scale: 1.5, z: 280, duration: 0.7, ease: "power1.in" }, 0.75);

      /* ----- Beat 2: Statements gleiten von links/rechts aus der Tiefe ein ----- */
      tl.addLabel("s2", 1.3);
      tl.set(s2.current, { autoAlpha: 1 }, 1.3);
      tl.to(s2Kicker.current, { autoAlpha: 1, y: 0, x: 0, duration: 0.4, ease: "power2.out" }, 1.35);
      lines.forEach((ln, i) => {
        tl.to(ln, { autoAlpha: 1, z: 0, y: 0, x: 0, duration: 0.5, ease: "power2.out" }, 1.45 + i * 0.26);
      });
      tl.to(s2Tail.current, { autoAlpha: 0.9, duration: 0.35 }, 2.6);

      /* ===== MATCH-CUT 1 — Worte werden zu Modulen =====
         Jedes Wort schrumpft und reist exakt zur Position seines Moduls,
         während das Modul dort aus der Tiefe heranwächst. */
      tl.to([s2Kicker.current, s2Tail.current], { autoAlpha: 0, y: -36, duration: 0.4, ease: "power1.in" }, 3.2);
      lines.forEach((ln, i) => {
        const st = 3.25 + i * 0.07;
        tl.to(ln, { x: lineTargets[i].x, y: lineTargets[i].y, scale: 0.3, duration: 0.65, ease: "power2.inOut" }, st);
        tl.to(ln, { autoAlpha: 0, duration: 0.42, ease: "power2.in" }, st + 0.22);
      });
      tl.addLabel("s3", 3.25);
      tl.set(s3.current, { autoAlpha: 1 }, 3.25);
      tl.to(s3Head.current, { autoAlpha: 1, y: 0, duration: 0.4, ease: "power2.out" }, 3.7);
      modules.forEach((m, i) => {
        tl.to(
          m,
          { z: MODULE_POS[i].z, scale: 1, autoAlpha: 1, duration: 0.6, ease: "power2.out" },
          3.5 + i * 0.07
        );
      });
      tl.set(s2.current, { autoAlpha: 0 }, 4.1);
      s3Lines.forEach((p, i) => {
        tl.to(p, { strokeDashoffset: 0, duration: 0.45, ease: "power1.inOut" }, 4.2 + i * 0.07);
      });
      // Tiefen-Parallax während des Holds: Vordergrund driftet schneller als Hintergrund
      modules.forEach((m, i) => {
        tl.to(m, { y: MODULE_POS[i].y - driftFor(i), duration: 0.5, ease: "none" }, 4.7);
      });

      /* ===== MATCH-CUT 2 — Module werden zu Prozess-Nodes =====
         Module ziehen sich zu leuchtenden Dots zusammen; die Dots reisen
         auf die Prozesslinie und markieren die Stellen, an denen die
         echten Nodes materialisieren. */
      tl.to(s3Head.current, { autoAlpha: 0, y: -30, duration: 0.4, ease: "power1.in" }, 5.3);
      tl.to(s3Lines, { autoAlpha: 0, duration: 0.3 }, 5.3);
      modules.forEach((m, i) => {
        const st = 5.3 + i * 0.05;
        tl.to(m, { scale: 0.08, autoAlpha: 0, duration: 0.5, ease: "power2.in" }, st);
        // Dot erscheint dort, wo das Modul kollabiert
        tl.set(dots[i], { x: MODULE_POS[i].x, y: MODULE_POS[i].y - driftFor(i), z: 0 }, st + 0.15);
        tl.to(dots[i], { autoAlpha: 1, duration: 0.15 }, st + 0.18);
        // ... und reist zur Position "seines" Prozess-Nodes (Nodes 1–4)
        tl.to(dots[i], { x: nodeTargets[i + 1].x, y: nodeTargets[i + 1].y, duration: 0.55, ease: "power2.inOut" }, st + 0.38);
      });
      tl.set(s3.current, { autoAlpha: 0 }, 6.0);

      /* ----- Beat 4: Die Linie zeichnet sich durch die gelandeten Dots ----- */
      tl.addLabel("s4", 6.0);
      tl.set(s4.current, { autoAlpha: 1 }, 6.0);
      tl.to(s4Head.current, { autoAlpha: 1, y: 0, duration: 0.35, ease: "power2.out" }, 6.05);
      tl.to(s4Path.current, { strokeDashoffset: 0, duration: 1.1, ease: "power1.inOut" }, 6.15);
      nodes.forEach((n, i) => {
        tl.to(n, { autoAlpha: 1, scale: 1, y: 0, x: 0, duration: 0.35, ease: "back.out(1.6)" }, 6.2 + i * 0.18);
      });
      // Dots lösen sich auf, sobald der echte Node auf ihnen materialisiert
      dots.forEach((d, i) => {
        tl.to(d, { autoAlpha: 0, scale: 1.6, duration: 0.25, ease: "power1.out" }, 6.42 + i * 0.18);
      });
      tl.to(s4Title.current, { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "power2.out" }, 6.65);

      /* ===== MATCH-CUT 3 — Die Prozesslinie zieht weiter zum Proof of Work =====
         Der Dash läuft nach rechts aus dem Bild, die Nodes verabschieden sich
         in Linienrichtung, ein Energie-Dot verlässt den Launch-Node und
         die Proof-Headline wächst aus seinem Landepunkt. */
      tl.to(s4Head.current, { autoAlpha: 0, y: -24, duration: 0.35, ease: "power1.in" }, 7.95);
      tl.to(s4Path.current, { strokeDashoffset: -100, duration: 0.7, ease: "power1.in" }, 7.95);
      nodes.forEach((n, i) => {
        tl.to(n, { autoAlpha: 0, y: 16, duration: 0.3, ease: "power1.in" }, 7.95 + i * 0.06);
      });
      tl.to(s4Title.current, { autoAlpha: 0, y: -20, duration: 0.4, ease: "power1.in" }, 8.05);
      tl.set(s4.current, { autoAlpha: 0 }, 8.6);

      /* ----- Beat 5: Proof of Work → Übergabe an die Project Wall ----- */
      tl.addLabel("s5", 8.6);
      tl.set(s5.current, { autoAlpha: 1 }, 8.6);
      tl.to(s5Head.current, { autoAlpha: 1, y: 0, scale: 1, duration: 0.5, ease: "power2.out" }, 8.8);
      chips.forEach((c, i) => {
        tl.to(c, { autoAlpha: 1, y: 0, z: 0, x: 0, duration: 0.4, ease: "power2.out" }, 9.0 + i * 0.1);
      });
      tl.to(s5Hint.current, { autoAlpha: 1, duration: 0.3 }, 9.75);
      tl.to({}, { duration: 0.35 }); // ruhiges Settle vor dem Unpin

      /* ----- Kamera-Pans: S2 leicht links, S3 leicht rechts, S4 wieder zentriert ----- */
      tl.to(camera.current, { x: -45, rotateY: 1.1, duration: 0.9, ease: "power1.inOut" }, 1.3);
      tl.to(camera.current, { x: 50, rotateY: -1.3, duration: 0.9, ease: "power1.inOut" }, 3.5);
      tl.to(camera.current, { x: 0, rotateY: 0, duration: 0.9, ease: "power1.inOut" }, 5.9);

      /* ===== CloudService Spark — eine Energie reist durch den ganzen Film =====
         Wordmark → durch die 4 Statements → Zentrum des Modul-Netzwerks →
         entlang der Prozesslinie → Proof-Headline → hinab zu den Referenzen. */
      tl.to(spark.current, { autoAlpha: 1, duration: 0.25 }, 0.15);
      tl.to(spark.current, { x: lineSparks[0].x, y: lineSparks[0].y, duration: 0.55, ease: "power2.inOut" }, 0.95);
      tl.to(spark.current, { x: lineSparks[1].x, y: lineSparks[1].y, duration: 0.28, ease: "power2.inOut" }, 1.78);
      tl.to(spark.current, { x: lineSparks[2].x, y: lineSparks[2].y, duration: 0.28, ease: "power2.inOut" }, 2.06);
      tl.to(spark.current, { x: lineSparks[3].x, y: lineSparks[3].y, duration: 0.28, ease: "power2.inOut" }, 2.34);
      // Match-Cut 1: Spark wird zum Zentrum, aus dem die Modul-Linien wachsen
      tl.to(spark.current, { x: 0, y: 0, duration: 0.6, ease: "power2.inOut" }, 3.3);
      // Match-Cut 2: Spark reist zum Startpunkt der Prozesslinie (Idee)
      tl.to(spark.current, { x: nodeTargets[0].x, y: nodeTargets[0].y, duration: 0.55, ease: "power2.inOut" }, 5.45);
      // Beat 4: Spark laeuft synchron zur Linienzeichnung bis zum Launch-Node
      tl.to(spark.current, { x: nodeTargets[5].x, y: nodeTargets[5].y, duration: 1.1, ease: "power1.inOut" }, 6.2);
      // Match-Cut 3: Spark verlaesst den Launch-Node, die Proof-Headline waechst aus ihm
      tl.to(spark.current, { x: 0, y: 62, duration: 0.5, ease: "power2.inOut" }, 8.35);
      tl.to(spark.current, { scale: 1.9, duration: 0.18, ease: "power1.out" }, 8.85);
      tl.to(spark.current, { scale: 1, duration: 0.2 }, 9.05);
      // Beat 5: Spark sinkt zum Referenzen-Hint und uebergibt an die Project Wall
      tl.to(spark.current, { y: 310, autoAlpha: 0, duration: 0.6, ease: "power2.in" }, 9.6);

      // ---------- Hintergrund driftet deutlich langsamer als der Vordergrund ----------
      const total = tl.duration();
      tl.to(bgRef.current, { yPercent: -6, duration: total }, 0);
      tl.to(glowRef.current, { x: 520, y: 280, duration: total }, 0);

      // progress-rail boundaries from labels
      boundsRef.current = ["s2", "s3", "s4", "s5"].map((l) => tl.labels[l] / total);
    }, sec);
    return () => ctx.revert();
  }, []);

  const railItems = ["01", "02", "03", "04", "05"];

  return (
    <section
      id="leistungen"
      ref={sec}
      data-testid="intro-film-section"
      className="relative h-screen overflow-hidden bg-[#05070d]"
    >
      {/* Persistente Film-Bühne: Grid, Glow, Orbits */}
      <div ref={bgRef} className="absolute inset-[-12%] cs-grid-bg opacity-70 pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 cs-glow-cyan pointer-events-none" aria-hidden="true" />
      <div className="absolute inset-0 cs-glow-indigo opacity-70 pointer-events-none" aria-hidden="true" />
      <div className="cs-orbits" aria-hidden="true">
        <div className="cs-orbit cs-orbit-a" />
        <div className="cs-orbit cs-orbit-b" />
      </div>
      <div
        ref={glowRef}
        aria-hidden="true"
        className="absolute left-[8%] top-[18%] w-[540px] h-[540px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(0,212,255,0.13), transparent 70%)" }}
      />

      {/* Progress rail */}
      <div
        data-testid="intro-progress-rail"
        className="absolute right-6 lg:right-10 top-1/2 -translate-y-1/2 z-30 flex flex-col items-center gap-4"
        aria-hidden="true"
      >
        {railItems.map((n, i) => (
          <div key={n} className="flex items-center gap-2">
            <span
              className={`text-[10px] font-mono-cs transition-colors duration-300 ${
                scene === i ? "text-[#00d4ff]" : "text-muted-foreground/40"
              }`}
            >
              {n}
            </span>
            <span
              className={`block w-1.5 h-1.5 rounded-full transition-[background-color,box-shadow] duration-300 ${
                scene === i ? "bg-[#00d4ff] shadow-[0_0_10px_rgba(0,212,255,0.8)]" : "bg-white/15"
              }`}
            />
          </div>
        ))}
      </div>

      {/* 3D-Bühne mit Kamera-Wrapper (subtile Pans zwischen den Szenen) */}
      <div className="absolute inset-0" style={{ perspective: "1400px" }}>
        <div ref={camera} className="absolute inset-0 will-change-transform" style={{ transformStyle: "preserve-3d" }}>
        {/* ===== Szene 1 — CloudService erscheint ===== */}
        <div
          ref={s1}
          data-testid="intro-scene-1"
          className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
          style={{ transformStyle: "preserve-3d" }}
        >
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease }}
            className="text-[11px] sm:text-xs font-mono-cs uppercase tracking-[0.3em] text-[#00d4ff]"
            data-testid="intro-s1-kicker"
          >
            {t.intro.s1Kicker}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.3, ease }}
          >
            <BigWordmark className="mt-6 text-6xl lg:text-[6.5rem]" />
            <span className="sr-only">— AI Solutions. SaaS Platforms. Mobile Apps. Business Automation.</span>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.55, ease }}
            className="mt-7 text-base lg:text-lg text-muted-foreground max-w-xl"
            data-testid="intro-s1-subline"
          >
            {t.intro.s1Sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="absolute bottom-10 flex flex-col items-center gap-2 text-muted-foreground/70"
            data-testid="intro-scroll-hint"
          >
            <span className="text-[10px] font-mono-cs uppercase tracking-[0.25em]">{t.intro.s1Scroll}</span>
            <motion.span
              animate={{ y: [0, 7, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            >
              <ChevronDown className="w-4 h-4 text-[#00d4ff]" />
            </motion.span>
          </motion.div>
        </div>

        {/* ===== Szene 2 — Positionierung (linksbündig, Kamera schwenkt) ===== */}
        <div
          ref={s2}
          data-testid="intro-scene-2"
          className="absolute inset-0 flex flex-col items-start justify-center text-left px-[10%] xl:px-[14%] pointer-events-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          <p ref={s2Kicker} className="text-[11px] font-mono-cs uppercase tracking-[0.28em] text-[#00d4ff] mb-8">
            {t.intro.s2Kicker}
          </p>
          <div style={{ transformStyle: "preserve-3d" }}>
            {t.intro.s2Lines.map((line, i) => (
              <div
                key={line}
                ref={(el) => (lineRefs.current[i] = el)}
                data-testid={`intro-s2-line-${i}`}
                className={`font-display font-semibold tracking-[-0.03em] leading-[1.12] text-4xl lg:text-6xl will-change-transform ${
                  i % 2 === 0 ? "text-foreground" : "text-foreground/55"
                }`}
              >
                {line}
              </div>
            ))}
          </div>
          <p ref={s2Tail} className="mt-9 text-sm font-mono-cs text-muted-foreground tracking-[0.12em]">
            {t.intro.s2Tail}
          </p>
        </div>

        {/* ===== Szene 3 — Module in der 3D-Szene ===== */}
        <div
          ref={s3}
          data-testid="intro-scene-3"
          className="absolute inset-0 pointer-events-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div ref={s3Head} className="absolute top-[12%] left-0 right-0 text-center px-6 z-20">
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.28em] text-[#00d4ff]">{t.intro.s3Kicker}</p>
            <h2 className="mt-2.5 font-display text-2xl lg:text-3xl font-semibold text-foreground">{t.intro.s3Title}</h2>
          </div>
          {/* Verbindungslinien vom Zentrum zu den Modulen */}
          <svg
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1000px] h-[600px] max-w-none"
            viewBox="-500 -300 1000 600"
            aria-hidden="true"
          >
            {MODULE_POS.map((p, i) => (
              <line
                key={i}
                ref={(el) => (s3LineRefs.current[i] = el)}
                x1="0"
                y1="0"
                x2={p.x}
                y2={p.y}
                pathLength="100"
                stroke="rgba(0,212,255,0.22)"
                strokeWidth="1"
                strokeDasharray="100"
              />
            ))}
            <circle cx="0" cy="0" r="3.5" fill="#00d4ff" opacity="0.7" />
          </svg>
          {/* Module — absolut im Zentrum, GSAP verteilt sie räumlich */}
          <div className="absolute inset-0 flex items-center justify-center" style={{ transformStyle: "preserve-3d" }}>
            {t.pillars.items.map((item, i) => {
              const Icon = MODULE_ICONS[i];
              return (
                <div
                  key={item.title}
                  ref={(el) => (moduleRefs.current[i] = el)}
                  className="absolute left-1/2 top-1/2 will-change-transform"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  {i === 2 ? (
                    <PhoneModule item={item} testId="intro-module-mobile" />
                  ) : (
                    <ModulePanel
                      item={item}
                      icon={Icon}
                      withFlow={i === 3}
                      dim={i === 1}
                      testId={`intro-module-${i === 0 ? "ai" : i === 1 ? "saas" : "automation"}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* ===== Szene 4 — Vom Konzept zum Produkt ===== */}
        <div
          ref={s4}
          data-testid="intro-scene-4"
          className="absolute inset-0 flex flex-col items-center justify-center px-6 pointer-events-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div ref={s4Head} className="text-center">
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.28em] text-[#00d4ff]">{t.intro.s4Kicker}</p>
          </div>
          <div className="relative mt-12 w-full max-w-[960px]">
            <svg className="absolute inset-x-0 top-[26px] w-full h-[60px]" viewBox="0 0 960 60" preserveAspectRatio="none" aria-hidden="true">
              <path
                ref={s4Path}
                d="M30 30 C 180 6, 300 54, 480 30 S 800 8, 930 30"
                fill="none"
                stroke="rgba(0,212,255,0.35)"
                strokeWidth="1.5"
                pathLength="100"
                strokeDasharray="100"
              />
            </svg>
            <div className="relative grid grid-cols-6 gap-2">
              {t.intro.s4Steps.map((step, i) => {
                const Icon = STEP_ICONS[i];
                return (
                  <div
                    key={step}
                    ref={(el) => (nodeRefs.current[i] = el)}
                    data-testid={`intro-s4-node-${i}`}
                    className="flex flex-col items-center gap-3 will-change-transform"
                  >
                    <span className="w-[52px] h-[52px] rounded-2xl cs-glass flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#00d4ff]" />
                    </span>
                    <span className="text-xs font-mono-cs uppercase tracking-[0.14em] text-foreground/85">{step}</span>
                  </div>
                );
              })}
            </div>
          </div>
          <h2
            ref={s4Title}
            data-testid="intro-s4-title"
            className="mt-14 font-display text-2xl lg:text-4xl font-semibold text-foreground text-center max-w-2xl tracking-[-0.02em]"
          >
            {t.intro.s4Title}
          </h2>
        </div>

        {/* ===== Szene 5 — Proof of Work ===== */}
        <div
          ref={s5}
          data-testid="intro-scene-5"
          className="absolute inset-0 flex flex-col items-center justify-center px-6 pointer-events-none"
          style={{ transformStyle: "preserve-3d" }}
        >
          <div ref={s5Head} className="text-center">
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.28em] text-[#00d4ff]">{t.intro.s5Kicker}</p>
            <h2 className="mt-3 font-display text-3xl lg:text-5xl font-semibold text-foreground tracking-[-0.025em]">
              {t.intro.s5Title}
            </h2>
            <p className="mt-3 text-sm lg:text-base text-muted-foreground">{t.intro.s5Sub}</p>
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-center gap-3.5 max-w-3xl" style={{ transformStyle: "preserve-3d" }}>
            {PROJECTS.map((p, i) => (
              <ProofChip key={p.id} project={p} innerRef={(el) => (chipRefs.current[i] = el)} />
            ))}
          </div>
          <div ref={s5Hint} className="absolute bottom-10 flex flex-col items-center gap-2 text-muted-foreground/70">
            <span className="text-[10px] font-mono-cs uppercase tracking-[0.25em]">{t.intro.s5Next}</span>
            <ChevronDown className="w-4 h-4 text-[#00d4ff]" />
          </div>
        </div>

        {/* ===== Bridge-Layer — verbindende Motion-Dots zwischen den Beats ===== */}
        <div className="absolute inset-0 pointer-events-none" style={{ transformStyle: "preserve-3d" }} aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              ref={(el) => (dotRefs.current[i] = el)}
              className="absolute left-1/2 top-1/2 w-2.5 h-2.5 rounded-full bg-[#00d4ff] shadow-[0_0_14px_rgba(0,212,255,0.9)] will-change-transform"
            />
          ))}
          {/* CloudService Spark — der durchgehende Energiepunkt des Films */}
          <span ref={spark} data-testid="intro-spark" className="absolute left-1/2 top-1/2 will-change-transform">
            <span className="cs-spark-core" />
          </span>
        </div>
        </div>
      </div>
    </section>
  );
};

/* =====================================================================
   MOBILE / REDUCED MOTION — gestapelte, leichte Szenen
   ===================================================================== */
const fadeUp = (reduced) => ({
  initial: reduced ? false : { opacity: 0, y: 26 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease },
});

const IntroFilmStacked = ({ reduced }) => {
  const { t } = useLang();
  const fp = fadeUp(reduced);

  return (
    <section id="leistungen" data-testid="intro-stacked-section" className="relative bg-[#05070d] overflow-hidden">
      <div className="absolute inset-0 cs-grid-bg opacity-60" aria-hidden="true" />
      <div className="absolute inset-0 cs-glow-cyan" aria-hidden="true" />

      {/* Szene 1 — Brand */}
      <div className="relative min-h-[88vh] flex flex-col items-center justify-center text-center px-6 pt-24 pb-16" data-testid="intro-stacked-s1">
        <motion.p {...fp} className="text-[11px] font-mono-cs uppercase tracking-[0.26em] text-[#00d4ff]">
          {t.intro.s1Kicker}
        </motion.p>
        <motion.div {...fp} transition={{ ...fp.transition, delay: 0.1 }}>
          <BigWordmark className="mt-5 text-[2.7rem] sm:text-6xl" />
          <span className="sr-only">— AI Solutions. SaaS Platforms. Mobile Apps. Business Automation.</span>
        </motion.div>
        <motion.p {...fp} transition={{ ...fp.transition, delay: 0.2 }} className="mt-6 text-base text-muted-foreground max-w-md">
          {t.intro.s1Sub}
        </motion.p>
      </div>

      {/* Szene 2 — Positionierung */}
      <div className="relative px-6 py-16 text-center" data-testid="intro-stacked-s2">
        <motion.p {...fp} className="text-[11px] font-mono-cs uppercase tracking-[0.26em] text-[#00d4ff] mb-6">
          {t.intro.s2Kicker}
        </motion.p>
        {t.intro.s2Lines.map((line, i) => (
          <motion.div
            key={line}
            initial={reduced ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, delay: i * 0.08, ease }}
            className={`font-display font-semibold tracking-[-0.03em] leading-[1.15] text-3xl sm:text-4xl ${
              i % 2 === 0 ? "text-foreground" : "text-foreground/55"
            }`}
          >
            {line}
          </motion.div>
        ))}
        <motion.p {...fp} className="mt-7 text-xs font-mono-cs text-muted-foreground tracking-[0.12em]">
          {t.intro.s2Tail}
        </motion.p>
      </div>

      {/* Szene 3 — Module */}
      <div className="relative px-6 py-16" data-testid="intro-stacked-s3">
        <motion.div {...fp} className="text-center">
          <p className="text-[11px] font-mono-cs uppercase tracking-[0.26em] text-[#00d4ff]">{t.intro.s3Kicker}</p>
          <h2 className="mt-2.5 font-display text-2xl font-semibold text-foreground">{t.intro.s3Title}</h2>
        </motion.div>
        <div className="mt-10 flex flex-col items-center gap-5 max-w-md mx-auto">
          {t.pillars.items.map((item, i) => {
            const Icon = MODULE_ICONS[i];
            return (
              <motion.div
                key={item.title}
                initial={reduced ? false : { opacity: 0, y: 28, scale: 0.96 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.65, delay: i * 0.06, ease }}
                className={i % 2 === 0 ? "self-start" : "self-end"}
              >
                {i === 2 ? (
                  <PhoneModule item={item} testId="intro-stacked-module-mobile" />
                ) : (
                  <ModulePanel item={item} icon={Icon} withFlow={i === 3} testId={`intro-stacked-module-${i}`} />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Szene 4 — Prozess */}
      <div className="relative px-6 py-16" data-testid="intro-stacked-s4">
        <motion.p {...fp} className="text-center text-[11px] font-mono-cs uppercase tracking-[0.26em] text-[#00d4ff]">
          {t.intro.s4Kicker}
        </motion.p>
        <div className="mt-9 max-w-xs mx-auto relative">
          <div className="absolute left-[25px] top-2 bottom-2 w-px bg-gradient-to-b from-[#00d4ff]/40 via-[#00d4ff]/15 to-transparent" aria-hidden="true" />
          <div className="space-y-5">
            {t.intro.s4Steps.map((step, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <motion.div
                  key={step}
                  initial={reduced ? false : { opacity: 0, x: -18 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.55, delay: i * 0.05, ease }}
                  className="relative flex items-center gap-4"
                >
                  <span className="w-[50px] h-[50px] rounded-2xl cs-glass flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#00d4ff]" />
                  </span>
                  <span className="text-sm font-mono-cs uppercase tracking-[0.14em] text-foreground/85">{step}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
        <motion.h2 {...fp} className="mt-12 font-display text-2xl font-semibold text-foreground text-center max-w-md mx-auto tracking-[-0.02em]">
          {t.intro.s4Title}
        </motion.h2>
      </div>

      {/* Szene 5 — Proof of Work */}
      <div className="relative px-6 pt-16 pb-20 text-center" data-testid="intro-stacked-s5">
        <motion.div {...fp}>
          <p className="text-[11px] font-mono-cs uppercase tracking-[0.26em] text-[#00d4ff]">{t.intro.s5Kicker}</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-foreground tracking-[-0.02em]">{t.intro.s5Title}</h2>
          <p className="mt-3 text-sm text-muted-foreground">{t.intro.s5Sub}</p>
        </motion.div>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3 max-w-md mx-auto">
          {PROJECTS.map((p, i) => (
            <motion.div
              key={p.id}
              initial={reduced ? false : { opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease }}
            >
              <ProofChip project={p} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CloudServiceIntroFilm = () => {
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();

  if (reduced || !isDesktop) return <IntroFilmStacked reduced={reduced} />;
  return <IntroFilmDesktop />;
};
